import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const PLAN_LABELS = {
  free: { label: 'Free', color: 'var(--text-muted)' },
  scout: { label: 'Scout Pro', color: '#00d4ff' },
  team: { label: 'Team Plan', color: '#a855f7' },
};

function Toast({ message, type }) {
  return (
    <div style={{
      position: 'fixed', top: 80, right: 24, zIndex: 9999,
      background: type === 'success' ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)',
      border: `1px solid ${type === 'success' ? '#10b981' : '#ef4444'}`,
      borderRadius: 10, padding: '14px 20px',
      color: type === 'success' ? '#10b981' : '#ef4444',
      fontSize: 14, fontWeight: 600, maxWidth: 340,
      backdropFilter: 'blur(12px)',
    }}>
      {message}
    </div>
  );
}

export default function BillingPage() {
  const { user } = useAuth();
  const location = useLocation();
  const [billing, setBilling] = useState(null);
  const [loadingPortal, setLoadingPortal] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    document.title = 'Billing | NeuroRank';
    return () => { document.title = 'NeuroRank | Cognitive Combine'; };
  }, []);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('success') === '1') {
      setToast({ message: 'Payment successful! Your account has been updated.', type: 'success' });
    }
  }, [location.search]);

  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 5000);
      return () => clearTimeout(t);
    }
  }, [toast]);

  useEffect(() => {
    const token = localStorage.getItem('nr_token');
    if (!token) return;
    fetch('/api/billing/subscription', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then(setBilling)
      .catch(() => {});
  }, []);

  const openPortal = async () => {
    setLoadingPortal(true);
    try {
      const res = await fetch('/api/billing/portal', {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('nr_token')}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to open portal.');
      window.location.href = data.url;
    } catch (err) {
      setToast({ message: err.message, type: 'error' });
      setLoadingPortal(false);
    }
  };

  if (!user) {
    return (
      <div className="container" style={{ paddingTop: 60, textAlign: 'center' }}>
        <p className="text-muted">Please sign in to view your billing settings.</p>
      </div>
    );
  }

  const sub = billing?.subscription || user?.subscription || { plan: 'free', status: null };
  const credits = billing?.retestCredits ?? user?.retestCredits ?? 0;
  const featuredProfiles = billing?.featuredProfiles || user?.featuredProfiles || [];
  const planMeta = PLAN_LABELS[sub.plan] || PLAN_LABELS.free;

  return (
    <div style={{ paddingBottom: 80 }}>
      {toast && <Toast message={toast.message} type={toast.type} />}

      <div style={{ borderBottom: '1px solid var(--border)', padding: '32px 0 24px' }}>
        <div className="container">
          <h1 style={{ marginBottom: 4 }}>Billing & Subscription</h1>
          <p className="text-muted">Manage your plan, credits, and featured profiles.</p>
        </div>
      </div>

      <div className="container" style={{ paddingTop: 28 }}>

        {/* Current Plan */}
        <div className="card" style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
            <div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>Current Plan</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: 22, fontWeight: 900, color: planMeta.color }}>{planMeta.label}</span>
                {sub.status && sub.status !== 'canceled' && (
                  <span style={{
                    fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
                    background: sub.status === 'active' ? 'rgba(16,185,129,0.15)' : 'rgba(245,158,11,0.15)',
                    color: sub.status === 'active' ? '#10b981' : '#f59e0b',
                    textTransform: 'uppercase', letterSpacing: 1,
                  }}>
                    {sub.status}
                  </span>
                )}
              </div>
              {sub.currentPeriodEnd && sub.plan !== 'free' && (
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                  {sub.status === 'canceled' ? 'Access until' : 'Renews'}{' '}
                  {new Date(sub.currentPeriodEnd).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              {sub.plan !== 'free' && (
                <button
                  className="btn btn--ghost"
                  onClick={openPortal}
                  disabled={loadingPortal}
                >
                  {loadingPortal ? 'Opening…' : 'Manage Billing'}
                </button>
              )}
              {sub.plan === 'free' && (
                <Link to="/pricing" className="btn btn--primary" style={{ fontSize: 13 }}>
                  Upgrade Plan
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* Re-test Credits */}
        <div className="card" style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
            <div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>Re-test Credits</div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span style={{ fontSize: 28, fontWeight: 900, color: credits > 0 ? '#10b981' : 'var(--text-muted)' }}>{credits}</span>
                <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>credit{credits !== 1 ? 's' : ''} remaining</span>
              </div>
              <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4, margin: '4px 0 0' }}>
                Each credit lets you skip the 24-hour cooldown and retake a combine immediately.
              </p>
            </div>
            <Link to="/pricing#boosts" className="btn btn--ghost" style={{ fontSize: 13 }}>
              Buy Credits — $1.99
            </Link>
          </div>
        </div>

        {/* Featured Profiles */}
        <div className="card" style={{ marginBottom: 20 }}>
          <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>Featured Profiles</div>
              <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>Profiles you've boosted appear first in scout search results.</p>
            </div>
            <Link to="/pricing#boosts" className="btn btn--ghost" style={{ fontSize: 13 }}>
              Boost a Profile — $4.99
            </Link>
          </div>

          {featuredProfiles.length === 0 ? (
            <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>No active profile boosts.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {featuredProfiles.map((fp) => {
                const isActive = fp.featuredUntil > Date.now();
                const daysLeft = Math.max(0, Math.ceil((fp.featuredUntil - Date.now()) / 86400000));
                return (
                  <div key={fp.shareableId} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '12px 16px', borderRadius: 10,
                    border: `1px solid ${isActive ? '#f59e0b44' : 'var(--border)'}`,
                    background: isActive ? 'rgba(245,158,11,0.05)' : 'var(--bg-secondary)',
                    flexWrap: 'wrap', gap: 10,
                  }}>
                    <div>
                      <span style={{ fontSize: 14, fontWeight: 700, color: isActive ? '#f59e0b' : 'var(--text-muted)', marginRight: 8 }}>★</span>
                      <a href={`/profile/${fp.shareableId}`} style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', textDecoration: 'none' }}>
                        {fp.shareableId}
                      </a>
                    </div>
                    <span style={{ fontSize: 12, color: isActive ? '#10b981' : 'var(--text-muted)' }}>
                      {isActive ? `${daysLeft} day${daysLeft !== 1 ? 's' : ''} remaining` : 'Expired'}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Upgrade CTA for free users */}
        {sub.plan === 'free' && (
          <div style={{
            borderRadius: 14, border: '1px solid #00d4ff33',
            background: 'rgba(0,212,255,0.05)', padding: '28px 24px', textAlign: 'center',
          }}>
            <h3 style={{ fontSize: 18, fontWeight: 800, marginBottom: 8 }}>Ready to scout talent?</h3>
            <p className="text-muted" style={{ marginBottom: 20 }}>
              Scout Pro unlocks full search, advanced filters, profile compare, and more.
            </p>
            <Link to="/pricing" className="btn btn--primary">
              Upgrade to Scout Pro — $19/mo
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
